import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, List, Card, CardContent, Chip, Stack, CircularProgress, Button, IconButton, TextField, MenuItem, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { useUser } from '../../contexts/PioAgentUserContext';
import { Menu } from '@mui/icons-material';
import { ThemeProvider, useTheme } from './context/ThemeContext';

import ChatInput from './input/ChatInput';
import ChatMessage from './message/ChatMessage';
import WelcomeSuggestions from './WelcomeSuggestions';
import Sidebar from './Sidebar';

import { useHealthCheck } from './hooks/useHealthCheck';
import { useModels } from './hooks/useModels';
import { useDocuments } from './hooks/useDocuments';
import { useSessionManagement } from './hooks/useSessionManagement';
import { useMessageHandling } from './hooks/useMessageHandling';
import { useTempFiles } from './hooks/useTempFiles';
import { customerSuggestions } from './constants/suggestions';

export default function ChatPage() {
  const { logout, isAdmin, apiConfigured, apiKey } = useUser();
  const navigate = useNavigate();
  const userRole = isAdmin ? 'admin' : 'user';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarFileInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const inputRef = useRef(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [copyFeedback, setCopyFeedback] = useState({});
  const [skipRag, setSkipRag] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(true);
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(true);

  // Get agent context from sessionStorage
  const [currentAgent, setCurrentAgent] = useState(null);

  const [initializationComplete, setInitializationComplete] = useState(false);
  const [initializationInProgress, setInitializationInProgress] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const agentContextStr = sessionStorage.getItem('pioagent_agent_context');
    if (agentContextStr) {
      try {
        const agentContext = JSON.parse(agentContextStr);
        setCurrentAgent(agentContext);
      } catch (error) {
        console.error('Error parsing agent context:', error);
      }
    }
  }, []);

  const {
    isSystemHealthy,
    healthServices,
    healthCheckLoading,
    lastHealthCheck,
    currentEndpoint,
    nodeInfo,
    healthCheckCompleted,
    performHealthCheck
  } = useHealthCheck();

  const {
    models,
    model,
    setModel,
    isModelsLoading,
    fetchModelsData,
    isCurrentModelThinkingModel
  } = useModels();

  const {
    documents,
    uploadLoading,
    currentPage,
    hasMorePages,
    isLoadingMoreDocs,
    totalDocCount,
    crawlUrlInput,
    setCrawlUrlInput,
    crawlLoading,
    crawlStatus,
    documentsEndRef,
    loadDocuments,
    handleFileUpload,
    handleDeleteDocument,
    handleCrawl,
    stopPolling
  } = useDocuments();

  const {
    sessionId,
    sessionIdRef,
    loadingSessionMessages,
    isLoadingOlderMessages,
    sessionMessagesPage,
    sessionTotalPages,
    refreshSessionsRef,
    createNewSession,
    loadSessionMessages,
    loadOlderMessages,
    handleNewSession
  } = useSessionManagement();

  const {
    handleSendMessage,
    handleFeedback,
    handleCopyText
  } = useMessageHandling();

  const {
    attachedFiles,
    handleFilesAttach,
    handleFileRemove,
    clearAttachedFiles,
    getReadyFileNames,
    hasUploadingFiles,
    hasPendingFiles,
    uploadFiles
  } = useTempFiles();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkIfUserAtBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < 50;
  };

  const handleScroll = () => {
    setIsUserAtBottom(checkIfUserAtBottom());
  };

  const initializeApplication = async () => {
    if (initializationInProgress || initializationComplete) {
      return;
    }

    setInitializationInProgress(true);

    try {
      await loadDocuments(1, true);

      await fetchModelsData();

      setInitializationComplete(true);
    } catch (error) {
      console.error('Application initialization failed:', error);
    } finally {
      setInitializationInProgress(false);
    }
  };

  useEffect(() => {
    if (!apiConfigured || !apiKey) {
      return;
    }

    performHealthCheck(true).then((isHealthy) => {
      if (isHealthy !== null) {
        if (isHealthy) {
          initializeApplication();
        }
      } else {
        initializeApplication();
      }
    });

    const healthCheckInterval = setInterval(() => {
      if (apiKey) {
        performHealthCheck(false);
      }
    }, 120000);

    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(healthCheckInterval);
      stopPolling();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [apiConfigured, apiKey]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const [previousSidebarState, setPreviousSidebarState] = useState(false);
  
  useEffect(() => {
    if (sidebarOpen && !previousSidebarState && isSystemHealthy && initializationComplete) {
      loadDocuments(1, true);
    }
    setPreviousSidebarState(sidebarOpen);
  }, [sidebarOpen, isSystemHealthy, initializationComplete, previousSidebarState]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUserAtBottom) {
        scrollToBottom();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [messages, isUserAtBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop === 0 && sessionMessagesPage && sessionMessagesPage > 1 && !isLoadingOlderMessages) {
        const scrollHeightBefore = container.scrollHeight;
        loadOlderMessages(setMessages).then(() => {
          requestAnimationFrame(() => {
            const scrollHeightAfter = container.scrollHeight;
            container.scrollTop = scrollHeightAfter - scrollHeightBefore;
          });
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sessionMessagesPage, isLoadingOlderMessages, loadOlderMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);


  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setHasTyped(true);
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
          }
  };

  const handleSend = async (audioFile = null, knowledge_base = null) => {
    const trimmedInput = input.trim();
    
    if (hasUploadingFiles()) {
      alert('Please wait for all files to finish uploading');
      return;
          }
        
    const tempFileNames = getReadyFileNames();
    
    console.log('[DEBUG] handleSend - Session ID:', sessionIdRef.current);
    console.log('[DEBUG] handleSend - Temp file names:', tempFileNames);
    console.log('[DEBUG] handleSend - Has pending files:', hasPendingFiles());

    abortControllerRef.current = new AbortController();
    
    setIsUserAtBottom(true);
    await handleSendMessage(
      sessionIdRef,
      createNewSession,
      trimmedInput,
      model,
      audioFile,
      knowledge_base !== null ? knowledge_base : useKnowledgeBase,
      searchEnabled,
      setMessages,
      setIsLoading,
      isCurrentModelThinkingModel,
      scrollToBottom,
      inputRef,
      setInput,
      tempFileNames,
      abortControllerRef.current,
      hasPendingFiles() ? uploadFiles : null
    );
    
    abortControllerRef.current = null;
    clearAttachedFiles();
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (!hasTyped && e.target.value !== '') {
      setHasTyped(true);
    }
  };
  
  const handleFileUploadWrapper = async (event) => {
    const files = Array.from(event.target.files || []);
    await handleFileUpload(files, setMessages);
      event.target.value = '';
  };

  const handleSessionSelect = async (selectedSessionId) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
    await loadSessionMessages(selectedSessionId, setMessages, setFeedbackGiven, setCopyFeedback, scrollToBottom);
  };

  const getSuggestions = () => {
    return customerSuggestions.default;
  };

  const handleNewSessionWrapper = () => {
    handleNewSession(setMessages, setFeedbackGiven, setCopyFeedback);
  };

  const handleLogout = () => {
    logout();
  };

  const handleSelectAgents = () => {
    // Check if we came from AgentStudio
    const agentContextStr = sessionStorage.getItem('pioagent_agent_context');
    if (agentContextStr) {
      // Clear agent context and navigate back to AgentStudio
      sessionStorage.removeItem('pioagent_agent_context');
      sessionStorage.removeItem('pioagent_current_agent');
      navigate('/agent-studio');
    } else {
      navigate('/pioagent/agents');
    }
  };

  const handleFeedbackWrapper = (messageId, isGood) => {
    handleFeedback(messageId, isGood, feedbackGiven, setFeedbackGiven);
  };

  const handleCopyTextWrapper = (text, messageId) => {
    handleCopyText(text, messageId, model, setCopyFeedback);
  };


  return (
    <ThemeProvider>
      <ChatPageContent
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        documents={documents}
        totalDocCount={totalDocCount}
        uploadLoading={uploadLoading}
        isLoadingMoreDocs={isLoadingMoreDocs}
        hasMorePages={hasMorePages}
        handleDeleteDocument={handleDeleteDocument}
        handleFileUploadWrapper={handleFileUploadWrapper}
        sidebarFileInputRef={sidebarFileInputRef}
        handleCrawl={handleCrawl}
        crawlUrlInput={crawlUrlInput}
        setCrawlUrlInput={setCrawlUrlInput}
        crawlLoading={crawlLoading}
        crawlStatus={crawlStatus}
        handleNewSessionWrapper={handleNewSessionWrapper}
        handleLogout={handleLogout}
        handleSelectAgents={handleSelectAgents}
        handleSessionSelect={handleSessionSelect}
        sessionId={sessionId}
        userRole={userRole}
        refreshSessionsRef={refreshSessionsRef}
        documentsEndRef={documentsEndRef}
        loadDocuments={loadDocuments}
        currentPage={currentPage}
        model={model}
        setModel={setModel}
        isModelsLoading={isModelsLoading}
        models={models}
        messagesContainerRef={messagesContainerRef}
        isLoadingOlderMessages={isLoadingOlderMessages}
        messages={messages}
        getSuggestions={getSuggestions}
        handleSuggestionClick={handleSuggestionClick}
        handleFeedbackWrapper={handleFeedbackWrapper}
        feedbackGiven={feedbackGiven}
        copyFeedback={copyFeedback}
        handleCopyTextWrapper={handleCopyTextWrapper}
        messagesEndRef={messagesEndRef}
        input={input}
        handleInputChange={handleInputChange}
        isLoading={isLoading}
        loadingSessionMessages={loadingSessionMessages}
        healthCheckLoading={healthCheckLoading}
        handleSend={handleSend}
        handleStopGeneration={handleStopGeneration}
        chatFileInputRef={chatFileInputRef}
        inputRef={inputRef}
        useKnowledgeBase={useKnowledgeBase}
        setUseKnowledgeBase={setUseKnowledgeBase}
        searchEnabled={searchEnabled}
        setSearchEnabled={setSearchEnabled}
        attachedFiles={attachedFiles}
        handleFilesAttach={handleFilesAttach}
        handleFileRemove={handleFileRemove}
        sessionIdRef={sessionIdRef}
        isSystemHealthy={isSystemHealthy}
        healthCheckLoading={healthCheckLoading}
        healthServices={healthServices}
        lastHealthCheck={lastHealthCheck}
        currentEndpoint={currentEndpoint}
        nodeInfo={nodeInfo}
        currentAgent={currentAgent}
      />
    </ThemeProvider>
  );
}

function ChatPageContent({
  sidebarOpen,
  setSidebarOpen,
  documents,
  totalDocCount,
  uploadLoading,
  isLoadingMoreDocs,
  hasMorePages,
  handleDeleteDocument,
  handleFileUploadWrapper,
  sidebarFileInputRef,
  handleCrawl,
  crawlUrlInput,
  setCrawlUrlInput,
  crawlLoading,
  crawlStatus,
  handleNewSessionWrapper,
  handleLogout,
  handleSelectAgents,
  handleSessionSelect,
  sessionId,
  userRole,
  refreshSessionsRef,
  documentsEndRef,
  loadDocuments,
  currentPage,
  model,
  setModel,
  isModelsLoading,
  models,
  messagesContainerRef,
  isLoadingOlderMessages,
  messages,
  getSuggestions,
  handleSuggestionClick,
  handleFeedbackWrapper,
  feedbackGiven,
  copyFeedback,
  handleCopyTextWrapper,
  messagesEndRef,
  input,
  handleInputChange,
  isLoading,
  loadingSessionMessages,
  healthCheckLoading,
  handleSend,
  handleStopGeneration,
  chatFileInputRef,
  inputRef,
  useKnowledgeBase,
  setUseKnowledgeBase,
  searchEnabled,
  setSearchEnabled,
  attachedFiles,
  handleFilesAttach,
  handleFileRemove,
  sessionIdRef,
  isSystemHealthy,
  healthCheckLoading: healthCheckLoadingProp,
  healthServices,
  lastHealthCheck,
  currentEndpoint,
  nodeInfo,
  currentAgent
}) {
  const { theme } = useTheme();

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex',
        height: '100vh',
        overflow: 'hidden'
      }}>
        <Sidebar
          isOpen={sidebarOpen}
          documents={documents}
          totalDocCount={totalDocCount}
          uploadLoading={uploadLoading}
          isLoadingMoreDocs={isLoadingMoreDocs}
          hasMorePages={hasMorePages}
          onDeleteDocument={handleDeleteDocument}
          onFileUpload={handleFileUploadWrapper}
          fileInputRef={sidebarFileInputRef}
          onCrawl={handleCrawl}
          crawlUrlInput={crawlUrlInput}
          setCrawlUrlInput={setCrawlUrlInput}
          crawlLoading={crawlLoading}
          crawlStatus={crawlStatus}
          onNewSession={handleNewSessionWrapper}
          onLogout={handleLogout}
          onSelectAgents={handleSelectAgents}
          onSessionSelect={handleSessionSelect}
          currentSessionId={sessionId}
          userRole={userRole}
          onRefreshSessions={(fn) => { refreshSessionsRef.current = fn; }}
          documentsEndRef={documentsEndRef}
          onLoadDocuments={() => loadDocuments(1, true)}
          onLoadMore={() => loadDocuments(currentPage + 1, false)}
        />
        
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: 'background.default',
          position: 'relative'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => setSidebarOpen(true)}
                size="medium"
                sx={{
                  display: { xs: sidebarOpen ? 'none' : 'flex', md: 'none' },
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  }
                }}
              >
                <Menu />
              </IconButton>

              <TextField
              select
              size="small"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isModelsLoading}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      maxWidth: { xs: '90vw', sm: 400 },
                      mt: 0.5,
                    }
                  }
                }
              }}
              sx={{
                minWidth: { xs: 180, sm: 220 },
                maxWidth: { xs: '70vw', sm: 'none' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }
                }
              }}
            >
              {models.map((modelOption) => (
                <MenuItem
                  key={modelOption.value}
                  value={modelOption.value}
                  disabled={!modelOption.isHealthy}
                  sx={{
                    fontSize: '0.875rem',
                    opacity: modelOption.isHealthy ? 1 : 0.5,
                    '&.Mui-disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {modelOption.label}
                    </Typography>
                    {!modelOption.isHealthy && (
                      <Chip
                        label="Down"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '18px' }}
                      />
                    )}
                    {modelOption.isThinkingModel && modelOption.isHealthy && (
                      <Chip
                        label="Thinking"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '18px' }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            </Box>

            {currentAgent && (
              <Box sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                minWidth: { xs: 120, sm: 160 }
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: 'primary.main',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Agent: {currentAgent.agentName}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box 
            ref={messagesContainerRef}
            sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            p: 2, 
              pt: { xs: 8, sm: 4 },
            maxWidth: '800px', 
            mx: 'auto', 
            width: '100%',
            scrollBehavior: 'smooth',
            position: 'relative',
            filter: (isSystemHealthy === false) ? 'grayscale(1) opacity(0.3)' : 'none',
            pointerEvents: (isSystemHealthy === false) ? 'none' : 'auto'
            }}
          >
            <List sx={{ display: 'flex', flexDirection: 'column' }}>
              {isLoadingOlderMessages && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {messages.length === 0 ? (
                <WelcomeSuggestions 
                  suggestions={getSuggestions()} 
                  onSuggestionClick={handleSuggestionClick} 
                />
              ) : (
                messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || `msg-${index}`}
                    message={message}
                    model={model}
                    showReferences={true}
                    onFeedback={handleFeedbackWrapper}
                    feedbackGiven={feedbackGiven}
                    copyFeedback={copyFeedback}
                    onCopy={handleCopyTextWrapper}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>
          
          <Box sx={{ 
            p: 2, 
            maxWidth: '800px', 
            mx: 'auto', 
            width: '100%',
            filter: (isSystemHealthy === false) ? 'grayscale(1) opacity(0.3)' : 'none',
            pointerEvents: (isSystemHealthy === false) ? 'none' : 'auto'
          }}>
            <ChatInput 
              input={input}
              onInputChange={handleInputChange}
              isLoading={isLoading || loadingSessionMessages || healthCheckLoading}
              onSend={handleSend}
              onStop={handleStopGeneration}
              onFileUpload={handleFileUploadWrapper}
              fileInputRef={chatFileInputRef}
              inputRef={inputRef}
              useKnowledgeBase={useKnowledgeBase}
              setUseKnowledgeBase={setUseKnowledgeBase}
              searchEnabled={searchEnabled}
              setSearchEnabled={setSearchEnabled}
              attachedFiles={attachedFiles}
              onFilesAttach={handleFilesAttach}
              onFileRemove={handleFileRemove}
              sessionIdRef={sessionIdRef}
            />
          </Box>
          
          <Typography variant="caption" sx={{ textAlign: 'center', py: 1, color: 'text.secondary' }}>
            The AI Assistant may make mistakes. Please verify important information.
          </Typography>
        </Box>
      </Box>
    </MuiThemeProvider>
  );
}
